import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { PageHeader, Card, Btn, Empty } from "../../components/UI";
import { SlidePanel, ConfirmDialog } from "../../components/SlidePanel";
import { FLabel, FInput, FSelect } from "../engagement/FormControls";
import { Plus, Trash2, Wand2, Mail } from "lucide-react";

const MSG_TYPES = ["custom", "reminder", "appointment", "broadcast"];

export default function ConfigurationLibrary() {
  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="configuration-library">
      <PageHeader title="Configuration Library" subtitle="Manage note types and message templates that show up across the app." />
      <div className="grid gap-6 mt-4">
        <NoteTypesCard />
        <MessageTemplatesCard />
      </div>
    </div>
  );
}

function NoteTypesCard() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ label: "", description: "", form_id: "" });
  const [del, setDel] = useState(null);
  const load = () => api.get("/note-types").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const save = async (e) => {
    e.preventDefault();
    try { await api.post("/note-types", form); toast.success("Note type added"); setOpen(false); setForm({ label: "", description: "", form_id: "" }); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const doDel = async () => { await api.delete(`/note-types/${del.id}`); toast.success("Deleted"); setDel(null); load(); };
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="flex items-center gap-2 font-heading text-base font-bold text-[#3E2723]"><Wand2 size={16} /> Chart Note Types</div>
          <p className="text-xs text-[#7B6157]">Adds clinic-specific note types on top of the built-in list (Progress Note, Nursing Note, and so on).</p>
        </div>
        <Btn data-testid="add-note-type-btn" onClick={() => setOpen(true)}><Plus size={14} /> Add Note Type</Btn>
      </div>
      {rows.length === 0 ? <Empty text="No custom note types yet. The built-in list is always available in the Add Chart Note dropdown." /> : (
        <div className="grid gap-2 md:grid-cols-2">
          {rows.map((r) => (
            <div key={r.id} data-testid={`note-type-row-${r.id}`} className="flex items-start justify-between gap-2 rounded-xl border border-[#EFEBE0] bg-white p-3">
              <div className="min-w-0">
                <div className="font-semibold text-sm text-[#3E2723]">{r.label}</div>
                {r.description && <div className="text-[11px] text-[#7B6157]">{r.description}</div>}
              </div>
              <button type="button" data-testid={`note-type-delete-${r.id}`} onClick={() => setDel(r)}
                className="h-8 w-8 rounded-full text-[#C62828] hover:bg-[#FFEBEE] flex items-center justify-center"><Trash2 size={13} /></button>
            </div>
          ))}
        </div>
      )}
      <SlidePanel open={open} onClose={() => setOpen(false)} title="Add Note Type" testId="add-note-type-panel">
        <form onSubmit={save} className="space-y-3">
          <div><FLabel>Label</FLabel><FInput data-testid="note-type-label-input" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} required placeholder="e.g., PHP Group Note" /></div>
          <div><FLabel>Description (optional)</FLabel><FInput data-testid="note-type-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Short description shown in tooltips" /></div>
          <div><FLabel>Linked Clinical Form ID (optional)</FLabel><FInput value={form.form_id} onChange={(e) => setForm({ ...form, form_id: e.target.value })} placeholder="Paste a form id from Clinical Forms Library" /></div>
          <div className="flex justify-end gap-2 border-t border-[#F1EEE7] pt-3">
            <Btn type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn>
            <Btn data-testid="note-type-save-btn" type="submit">Add</Btn>
          </div>
        </form>
      </SlidePanel>
      <ConfirmDialog open={!!del} onClose={() => setDel(null)} onConfirm={doDel} title="Delete note type" description={del ? `Remove "${del.label}"? Existing notes that already use this type keep their label.` : ""} />
    </Card>
  );
}

function MessageTemplatesCard() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ label: "", subject: "", body: "", message_type: "custom" });
  const [del, setDel] = useState(null);
  const load = () => api.get("/messages/templates").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const save = async (e) => {
    e.preventDefault();
    try { await api.post("/messages/templates", form); toast.success("Template added"); setOpen(false); setForm({ label: "", subject: "", body: "", message_type: "custom" }); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const doDel = async () => { await api.delete(`/messages/templates/${del.id}`); toast.success("Deleted"); setDel(null); load(); };
  const customs = rows.filter((r) => !r.is_builtin);
  const builtins = rows.filter((r) => r.is_builtin);
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="flex items-center gap-2 font-heading text-base font-bold text-[#3E2723]"><Mail size={16} /> Message Templates</div>
          <p className="text-xs text-[#7B6157]">Built-in templates ship with the app; custom templates are clinic-specific and reusable in the compose modal.</p>
        </div>
        <Btn data-testid="add-template-btn" onClick={() => setOpen(true)}><Plus size={14} /> Add Template</Btn>
      </div>
      <div className="space-y-4">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wide text-[#84675B] mb-1">Built-in ({builtins.length})</div>
          <div className="grid gap-2 md:grid-cols-2">
            {builtins.map((r) => <TemplateRow key={r.key} r={r} readonly />)}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wide text-[#84675B] mb-1">Clinic Custom ({customs.length})</div>
          {customs.length === 0 ? <Empty text="No custom templates yet." /> : (
            <div className="grid gap-2 md:grid-cols-2">
              {customs.map((r) => <TemplateRow key={r.id} r={r} onDelete={() => setDel(r)} />)}
            </div>
          )}
        </div>
      </div>
      <SlidePanel open={open} onClose={() => setOpen(false)} title="Add Message Template" testId="add-template-panel">
        <form onSubmit={save} className="space-y-3">
          <div><FLabel>Label</FLabel><FInput data-testid="template-label-input" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} required placeholder="e.g., Copay Reminder" /></div>
          <div><FLabel>Message Type</FLabel>
            <FSelect data-testid="template-type-input" value={form.message_type} onChange={(e) => setForm({ ...form, message_type: e.target.value })}>
              {MSG_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </FSelect>
          </div>
          <div><FLabel>Subject</FLabel><FInput data-testid="template-subject-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required /></div>
          <div><FLabel>Body</FLabel>
            <textarea data-testid="template-body-input" rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} required
              placeholder="Use {{first_name}}, {{last_name}}, {{appointment_date}} as placeholders."
              className="w-full rounded-lg border border-[#E5DFD3] bg-white px-3 py-2 text-sm outline-none focus:border-[#8D6E63]" />
          </div>
          <div className="flex justify-end gap-2 border-t border-[#F1EEE7] pt-3">
            <Btn type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn>
            <Btn data-testid="template-save-btn" type="submit">Add</Btn>
          </div>
        </form>
      </SlidePanel>
      <ConfirmDialog open={!!del} onClose={() => setDel(null)} onConfirm={doDel} title="Delete template" description={del ? `Remove "${del.label}"?` : ""} />
    </Card>
  );
}

function TemplateRow({ r, readonly, onDelete }) {
  return (
    <div data-testid={`template-row-${r.id || r.key}`} className="rounded-xl border border-[#EFEBE0] bg-white p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-sm text-[#3E2723] truncate">{r.label}</span>
            <span className="rounded-full bg-[#F5F2EB] px-2 py-0.5 text-[10px] font-bold text-[#7B6157]">{r.message_type}</span>
          </div>
          <div className="mt-0.5 text-[11px] text-[#7B6157] truncate">{r.subject}</div>
          <div className="mt-1 text-[11px] text-[#84675B] line-clamp-2">{r.body}</div>
        </div>
        {!readonly && onDelete && (
          <button type="button" data-testid={`template-delete-${r.id}`} onClick={onDelete}
            className="h-8 w-8 rounded-full text-[#C62828] hover:bg-[#FFEBEE] flex items-center justify-center"><Trash2 size={13} /></button>
        )}
      </div>
    </div>
  );
}
